import React, { useState, useEffect, useContext } from 'react';
import BasicContext from '../BasicContext';
import { Card } from 'antd-mobile';

import { Loading, Radios } from '../components';
import DateRangeButton from '../components/DateRangeButton';
import Statistic from '../components/Statistic';

import moment from 'moment';
import { ForeignKey, OrganizationType, ChannelOrganizationKey, CommonOrganizationKey, CHANNEL_CUSTOMER_ITID, MaxPage } from '../utils/const';
import { DescribeSendStatistics } from "../../../api/usms";
import { timeFormatType, getDateRange } from '../utils/timeFormat';
import { getSthousandsFormatNum, getPercentFormat } from '../utils/common';
import { GetCustomerExtendInfoList } from '../../../api/usms';

const dateList = ["当日", "本月", "上月"];
const defaultDateBtn = "当日";
const defaultDateRange = getDateRange("当日");
// 当前周期对应的上周期
const dateBeforeOptions = {
  "当日": "昨天",
  "本月": "上月",
  "上月": "上上月"
}
/**
 * 客户项目维度发送数据
 * 获取国家维度数据，国外为所有非CN数据总和
 */
function AccountOverview(props) {

  const context = useContext(BasicContext);
  const customerList = context.customers;

  const [filters, setFilters] = useState({
    dateRange: defaultDateRange,
    dateBtn: defaultDateBtn,
    domesticFlag: ForeignKey,
    organizationType: CommonOrganizationKey
  });

  // 项目维度发送数据
  const [accountStatistics, setAccountStatistics] = useState([]);
  const [accountLoading, setAccountLoading] = useState(false);

  // 客户扩展信息（存储在运营平台）
  const [customerExtendInfoList, setCustomerExtendInfoList] = useState([]);
  const [extendInfoLoading, setExtendInfoLoading] = useState(false);

  const [topStatistics, setTopStatistics] = useState([]);

  useEffect(() => {
    getCustomerExtendInfoList();
    // eslint-disable-next-line
  }, [])
  useEffect(() => {
    if (customerExtendInfoList.length > 0 && customerList.length > 0) {
      onFilterChange(filters);
    }
    // eslint-disable-next-line
  }, [customerExtendInfoList, customerList])

  // 获取客户扩展信息（仅渠道客户，其余的默认都是直客）
  const getCustomerExtendInfoList = () => {
    setExtendInfoLoading(true);
    setCustomerExtendInfoList([]);
    const data = {
      IndustryTagIds: [Number(CHANNEL_CUSTOMER_ITID)],
      Limit: MaxPage
    };
    GetCustomerExtendInfoList(data).then(res => {
      setCustomerExtendInfoList(res);
      setExtendInfoLoading(false);
    })
  }

  const getSendStatistics = (filters) => {
    setAccountLoading(true);
    setAccountStatistics([]);
    const data = {
      BeginTime: moment(filters.dateRange[0]).unix(),
      EndTime: moment(filters.dateRange[1]).unix(),
      DomesticFlag: filters.domesticFlag,
      GroupCondition: ["OrganizationId"]
    }
    // 上一周期的参数
    const beforeData = {
      DomesticFlag: filters.domesticFlag,
      GroupCondition: ["OrganizationId"]
    }
    // 有对应的上周期直接用上周期，否则取周期时间差作为上周期
    if (dateBeforeOptions[filters.dateBtn]) {
      const dateBeforeRange = getDateRange(dateBeforeOptions[filters.dateBtn]);
      beforeData.BeginTime = moment(dateBeforeRange[0]).unix();
      beforeData.EndTime = moment(dateBeforeRange[1]).unix();
    } else {
      const difference = filters.dateRange[1].unix() - filters.dateRange[0].unix(); // 时间差
      beforeData.BeginTime = moment(filters.dateRange[0]).unix() - difference - 1;
      beforeData.EndTime = moment(filters.dateRange[1]).unix() - difference - 1;
    }
    Promise.all([DescribeSendStatistics(data), DescribeSendStatistics(beforeData)]).then(([stats, beforeStats]) => {
      const formatStatistics = getFormatStatistics(stats, beforeStats);
      setAccountStatistics(formatStatistics);
      getTopStatistics(formatStatistics, filters);
      setAccountLoading(false);
    })
  }

  // 整理表格数据，只展示发送总量top10客户，成功率统一用提交数计算
  const getFormatStatistics = (statistics, beforeStatistics) => {
    // 整理表格数据
    const formatStatistics = statistics.map(item => {
      // 上周期数据
      const beforeData = beforeStatistics.find(bitem => bitem.OrganizationId === item.OrganizationId) || {};
      // 客户类型
      const organizationInfo = getOrganizationType(item.OrganizationId);
      const format = {
        OrganizationId: item.OrganizationId,
        OrganizationName: organizationInfo.Name,
        OrganizationType: organizationInfo.Type,
        CompanyId: organizationInfo.CompanyId,
        UserEmail: organizationInfo.UserEmail,
        Remark: organizationInfo.Remark,
        CostSendSuccess: item.SuccessCostCount,
        CostUnKnown: item.UnKnownCostCount,
        CostTotalCount: item.TotalCostCount,
        SuccessAndUnKnownPercent: getPercentFormat(item.SuccessCount + item.UnKnownCount, item.TotalCount),
        BeforeSuccessAndUnKnownPercent: getPercentFormat(beforeData.SuccessCount + beforeData.UnKnownCount, beforeData.TotalCount),
        SuccessPercent: getPercentFormat(item.SuccessCount, item.TotalCount),
        BeforeSuccessPercent: getPercentFormat(beforeData.SuccessCount, beforeData.TotalCount)
      }
      return format
    })

    return formatStatistics
  }

  // 获取项目的客户类型：直客/渠道客户，拓展信息是公司维度，转化为项目
  function getOrganizationType(accountId) {
    const company = customerList.find(item => item.OrganizationId === accountId) || {};
    const companyExtendInfo = customerExtendInfoList.find(item => item.CompanyId === company.CompanyId) || {};

    const info = {
      Name: company.OrganizationId ? `${company.OrganizationName}-${accountId}` : accountId,
      Type: companyExtendInfo.CompanyId ? ChannelOrganizationKey : CommonOrganizationKey,
      CompanyId: company.CompanyId,
      UserEmail: company.UserEmail,
      Remark: company.Remark
    }
    return info
  }

  function disabledDate(current) {
    const tooLate = current && current < moment().subtract(1, "years").startOf('day');
    const tooEarly = current && current > moment()
    return tooEarly || tooLate;
  }

  const getTopStatistics = (stats, filter) => {
    const sortStats = stats.sort((a, b) => b.CostTotalCount - a.CostTotalCount);
    const typeStats = sortStats.filter(item => item.OrganizationType === filter.organizationType);
    const topStats = typeStats.slice(0, 10);
    setTopStatistics(topStats);
  }

  const onFilterChange = (obj = {}, donotFetch) => {
    const fil = { ...filters };
    Object.keys(obj).forEach(item => {
      fil[item] = obj[item];
    });
    setFilters(fil);
    donotFetch ? getTopStatistics(accountStatistics, fil) : getSendStatistics(fil);
  }

  const Filter = () => <div style={{ marginTop: '10px' }}>
    <Radios
      size="small"
      value={filters.organizationType}
      onChange={value => onFilterChange({ organizationType: value }, true)}
      options={Object.keys(OrganizationType).map(item => ({ label: OrganizationType[item], value: item }))}
    />
    {/* <Radios
      size="small"
      style={{ marginLeft: '20px' }}
      value={filters.domesticFlag}
      onChange={value => onFilterChange({ domesticFlag: value })}
      options={Object.keys(ForeignFlag).map(item => ({ label: ForeignFlag[item], value: item }))}
    /> */}
    <DateRangeButton
      dateList={dateList}
      value={filters.dateRange}
      size="small"
      style={{ margin: '10px 0' }}
      onChange={(value, btn) => onFilterChange({ dateRange: value, dateBtn: btn })}
      defaultRangeBtn={filters.dateBtn}
      rangePickerOptions={{
        format: timeFormatType.date,
        disabledDate,
      }}
    />
  </div>

  return <Loading loading={accountLoading || extendInfoLoading}>
    <Filter />
    <Card size="small" >
      <Card.Header title="整体概览" />
      <Card.Body>
        {topStatistics.map((item, index) =>
          <Statistic
            key={index}
            style={{ marginBottom: '10px' }}
            data={`${item.Remark}(${item.CompanyId}), 项目：${item.OrganizationName}`}
            compareData={[
              {
                name: "提交成功率(含未知)",
                data: item.SuccessAndUnKnownPercent,
                beforeData: item.BeforeSuccessAndUnKnownPercent,
                labelWidth: '45%'
              },
              {
                name: "提交成功率(不含未知)",
                data: item.SuccessPercent,
                beforeData: item.BeforeSuccessPercent,
                labelWidth: '45%'
              },
              {
                name: "拆分发送条数",
                data: item.CostTotalCount,
                unit: "条",
                renderData: getSthousandsFormatNum,
                labelWidth: '45%'
              },
              {
                name: "拆分未知条数",
                data: item.CostUnKnown,
                unit: "条",
                renderData: getSthousandsFormatNum,
                labelWidth: '45%'
              },
              {
                name: "拆分成功条数",
                data: item.CostSendSuccess,
                unit: "条",
                renderData: getSthousandsFormatNum,
                labelWidth: '45%'
              }
            ]} />
        )}
      </Card.Body>
    </Card>
  </Loading>
}
export default AccountOverview